import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ServiceRow {
  id: string;
  brand_id: string;
  slug: string;
  name: string;
  short_description: string | null;
  long_description_html: string | null;
  hero_image_url: string | null;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ServiceInput {
  name: string;
  slug: string;
  short_description?: string | null;
  long_description_html?: string | null;
  hero_image_url?: string | null;
  icon?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

export function useServices(options?: { activeOnly?: boolean }) {
  return useQuery({
    queryKey: ["services", options?.activeOnly ?? false],
    queryFn: async () => {
      let q = supabase.from("services").select("*").order("sort_order").order("name");
      if (options?.activeOnly) q = q.eq("is_active", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ServiceRow[];
    },
  });
}

export function useUpdateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<ServiceInput>) => {
      const { error } = await supabase.from("services").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success("Service updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useCreateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ServiceInput) => {
      const { data, error } = await supabase
        .from("services")
        .insert({
          name: input.name.trim(),
          slug: input.slug.trim(),
          short_description: input.short_description ?? null,
          long_description_html: input.long_description_html ?? null,
          hero_image_url: input.hero_image_url ?? null,
          icon: input.icon ?? null,
          sort_order: input.sort_order ?? 0,
          is_active: input.is_active ?? true,
        })
        .select()
        .single();
      if (error) throw error;
      return data as ServiceRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success("Service added");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
